"use client";

/**
 * The client side of the recommender's logs (recommender plan, section
 * 2.2 and 5):
 *
 *  - logImpressions: the ordered ids a recommendation surface rendered,
 *    once per request id, fire-and-forget (POST /api/rec/impressions; the
 *    list does not fit an event's 140-character props).
 *  - hide / unhide: the "not for me" button and its undo.
 *  - withRec: adds `rec` (the request id) and `pos` to an event's props
 *    when the card came from a recommendation, so a click, a dwell or a
 *    contact joins back to the impression.
 *  - recFromSearch / recFromElement / recData: where that pair travels: a
 *    landing URL (`?via=digest&rec=<id>&pos=<n>`) or a card's
 *    `data-rec` / `data-pos` attributes.
 */
import { API_BASE } from "@/lib/api";
import { getClientId } from "@/lib/client-id";
import { IS_CRAWLER } from "@/lib/crawler";
import { currentTrackSessionId } from "@/lib/tracking";
import type { RecAnswer } from "@/lib/rec-feed";

export interface RecRef {
  /** The recommendation request id (a UUID). */
  rec?: string | null;
  /** The card's position in that list, from 0. */
  pos?: number | null;
}

export type HideReason = "price" | "area" | "agent" | "condition" | "place" | "other";

const REC_RE = /^[0-9a-f-]{8,36}$/i;

function cleanRec(rec: unknown): string | null {
  return typeof rec === "string" && REC_RE.test(rec) ? rec.toLowerCase() : null;
}

function cleanPos(pos: unknown): number | null {
  const n = typeof pos === "number" ? pos : typeof pos === "string" && pos !== "" ? Number(pos) : NaN;
  return Number.isInteger(n) && n >= 0 && n < 1000 ? n : null;
}

/** `props` plus `rec` and `pos` when `ref` carries a valid pair. */
export function withRec<T extends Record<string, unknown>>(props: T, ref?: RecRef | null): T & { rec?: string; pos?: number } {
  const rec = cleanRec(ref?.rec);
  if (!rec) return props;
  const pos = cleanPos(ref?.pos);
  return { ...props, rec, ...(pos != null ? { pos } : {}) };
}

/** The recommendation a landing URL carries (`rec`, `pos`), or null. */
export function recFromSearch(search: string): RecRef | null {
  try {
    const sp = new URLSearchParams(search);
    const rec = cleanRec(sp.get("rec"));
    return rec ? { rec, pos: cleanPos(sp.get("pos")) } : null;
  } catch {
    return null;
  }
}

/** The recommendation of the card an element sits in (`data-rec`). */
/** @public - the recommendation surfaces (recommender plan, PR C) call it */
export function recFromElement(el: Element | null | undefined): RecRef | null {
  const card = el?.closest?.("[data-rec]") as HTMLElement | null | undefined;
  const rec = cleanRec(card?.dataset.rec);
  return rec ? { rec, pos: cleanPos(card?.dataset.pos) } : null;
}

/** The data attributes a recommended card's link carries. */
export function recData(ref?: RecRef | null): { "data-rec"?: string; "data-pos"?: number } {
  const rec = cleanRec(ref?.rec);
  if (!rec) return {};
  const pos = cleanPos(ref?.pos);
  return { "data-rec": rec, ...(pos != null ? { "data-pos": pos } : {}) };
}

function headers(): Record<string, string> {
  return { "Content-Type": "application/json", "X-Client-Id": getClientId() };
}

// One POST per request id per tab: a surface re-rendering (Strict Mode's
// second effect run, a parent's state change) must not log the list twice.
const sent = new Set<string>();

/** @public - the recommendation surfaces (recommender plan, PR C) call it */
export function logImpressions(requestId: string, surface: string, algo: string, ids: readonly string[]): void {
  if (IS_CRAWLER || typeof window === "undefined") return;
  const rec = cleanRec(requestId);
  if (!rec || !ids.length || sent.has(rec)) return;
  sent.add(rec);
  const session = currentTrackSessionId();
  void fetch(`${API_BASE}/rec/impressions`, {
    method: "POST",
    headers: headers(),
    credentials: "include",
    keepalive: true,
    body: JSON.stringify({
      rec_request_id: rec,
      surface,
      algo,
      listing_ids: ids.slice(0, 50),
      ...(session ? { session_id: session } : {}),
    }),
  }).catch(() => { /* tracking never breaks the page */ });
}

/** @public - the recommendation surfaces (recommender plan, PR C) call it */
export async function hide(id: string, reason: HideReason, ref?: RecRef | null): Promise<boolean> {
  const rec = cleanRec(ref?.rec);
  const pos = cleanPos(ref?.pos);
  const session = currentTrackSessionId();
  try {
    const res = await fetch(`${API_BASE}/rec/hide`, {
      method: "POST",
      headers: headers(),
      credentials: "include",
      body: JSON.stringify({
        listing_id: id,
        reason,
        ...(rec ? { rec_request_id: rec } : {}),
        ...(rec && pos != null ? { position: pos } : {}),
        ...(session ? { session_id: session } : {}),
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** @public - the recommendation surfaces (recommender plan, PR C) call it */
export async function unhide(id: string): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/rec/hide/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: headers(),
      credentials: "include",
    });
    return res.ok;
  } catch {
    return false;
  }
}

// ── The recommendation reads (recommender plan, section 5) ──────────

/** GET /api/rec/for-you: the person's (or this visit's) list. `ctx` is
 *  the landing context the engine reads when there is no history yet
 *  (`search:<listings query>`, `residence:<slug>`, `place:<id>`). Null on
 *  any failure: the surface keeps its own list. */
export async function fetchForYou(
  { n, surface, ctx, signal }: { n: number; surface: string; ctx?: string | null; signal?: AbortSignal },
): Promise<RecAnswer | null> {
  if (IS_CRAWLER) return null;
  const qs = new URLSearchParams({ n: String(n), surface });
  if (ctx) qs.set("ctx", ctx.slice(0, 300));
  return readRec(`${API_BASE}/rec/for-you?${qs.toString()}`, signal);
}

/** GET /api/rec/similar/{id}: what people who opened this listing opened. */
export async function fetchRecSimilar(id: string, n: number, signal?: AbortSignal): Promise<RecAnswer | null> {
  if (IS_CRAWLER) return null;
  return readRec(`${API_BASE}/rec/similar/${encodeURIComponent(id)}?n=${n}`, signal);
}

async function readRec(url: string, signal?: AbortSignal): Promise<RecAnswer | null> {
  try {
    const res = await fetch(url, { headers: { "X-Client-Id": getClientId() }, credentials: "include", signal });
    if (!res.ok) return null;
    const body = (await res.json()) as RecAnswer;
    return body && Array.isArray(body.items) ? body : null;
  } catch {
    return null;
  }
}
